import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';
import useApiClient from '../hooks/api/useApiClient';
import useAuth from '../hooks/auth/useAuth';
import { BRAND_MODES, FOOD_AMIGOS_BRAND, NEUTRAL_BRAND } from '../services/branding/brands';
import { isFoodAmigosHost } from '../services/branding/hosts';
import { resolvePartnerSlug } from '../services/branding/partnerSlugStorage';
import { brandFromConfigResponse, brandFromServiceProvider } from '../services/branding/brandFromServiceProvider';
import { readCachedBrand, writeCachedBrand } from '../services/branding/brandCache';
import { applyBrandColor, clearBrandColor } from '../services/branding/brandTheme';
import { resolveBrandLanguage } from '../services/localization/brandLanguages';

export const BrandContext = createContext(null);

export { BRAND_MODES };

// Logged-out brand when neither the host nor a slug names a partner: FoodAmigos on its own
// hosts, elsewhere the brand last resolved on this browser (by host, slug or session), else
// the neutral identity.
function fallbackBrand(hostname) {
  if (isFoodAmigosHost(hostname)) {
    return FOOD_AMIGOS_BRAND;
  }

  return readCachedBrand(hostname) ?? NEUTRAL_BRAND;
}

// Off FoodAmigos' own hosts the hostname is sent to brand-config: it may be a partner's own
// dashboard host, which names the brand on its own. FoodAmigos hosts never are.
function brandConfigHost(hostname) {
  return isFoodAmigosHost(hostname) ? null : hostname;
}

function initialState({ isLoggedIn, serviceProvider, slug, hostname }) {
  if (isLoggedIn) {
    // The persisted provider (redux-persist, rehydrated before the first
    // render) brands the very first paint of a logged-in reload.
    return { brand: brandFromServiceProvider(serviceProvider) ?? fallbackBrand(hostname), settled: true };
  }

  if (!slug && !brandConfigHost(hostname)) {
    return { brand: fallbackBrand(hostname), settled: true };
  }

  // Branded by `GET /brand-config` — logged-out screens wait on `settled`. Start from the
  // cached brand (almost always the same partner) so a re-visit does not flash neutral.
  return { brand: readCachedBrand(hostname) ?? NEUTRAL_BRAND, settled: false };
}

/**
 * Brand source of truth:
 * - logged in  → the session's current service provider (`is_master` → FoodAmigos);
 * - logged out → brand-config, asked with the hostname (off FoodAmigos hosts) and the
 *                `?partner=<slug>` persisted per tab; the backend lets the host win.
 *                Neither → FoodAmigos on FoodAmigos hosts, elsewhere the brand cached on
 *                this browser, else neutral.
 *
 * The hostname picks a partner brand now: a partner can serve this SPA on their own
 * domain, where it is the stronger statement — a slug can be one this tab kept from
 * elsewhere. A host brand-config does not recognise (the shared white-label host, or a
 * partner's host whose DNS has not landed) answers neutral, and a neutral answer to the
 * host alone keeps the cached brand rather than blanking the login page. Only a host, a
 * slug or a session rewrites the cache.
 */
export default function BrandProvider({ children }) {
  const hostname = window.location.hostname;

  const { isLoggedIn } = useAuth();
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  // `useApiClient` re-creates its managers whenever the request config changes
  // (token, tenant headers, language). Read the manager through a ref so the
  // brand effects only depend on primitives + the provider row, never on the
  // manager identity — otherwise every config change re-fetched the brand.
  const { BrandConfigManager } = useApiClient({ autoLogOut: false });
  const managerRef = useRef(BrandConfigManager);

  useEffect(() => {
    managerRef.current = BrandConfigManager;
  }, [BrandConfigManager]);

  // Resolved synchronously on mount so a `?partner=` on a magic-link landing
  // is persisted before Router.validateToken() reloads the page.
  const [partnerSlug, setPartnerSlug] = useState(() => resolvePartnerSlug());
  const [state, setState] = useState(() => initialState({ isLoggedIn, serviceProvider, slug: partnerSlug, hostname }));

  // Logged out: brand from the partner slug (URL, else the one persisted by
  // this tab — incl. the one AuthProvider.logOut syncs from the session).
  // Without a slug the cached brand stays; nothing is cleared on logout.
  useEffect(() => {
    if (isLoggedIn) {
      return undefined;
    }

    const slug = resolvePartnerSlug();
    const host = brandConfigHost(hostname);
    setPartnerSlug(slug);

    if (!slug && !host) {
      setState({ brand: fallbackBrand(hostname), settled: true });

      return undefined;
    }

    let cancelled = false;
    setState((current) => ({ ...current, settled: false }));

    managerRef.current.get({ partner: slug, host }).then(({ success, data }) => {
      if (cancelled) {
        return;
      }

      if (!success) {
        // Unreachable brand-config: keep whatever brands the page already.
        setState((current) => ({ brand: current.brand, settled: true }));

        return;
      }

      const brand = brandFromConfigResponse(data);

      // Asked by host alone and answered neutral: not a host any partner serves on (yet).
      // Keep what this browser last resolved here — the shared host's bookmark visit keeps
      // the partner it has always shown.
      if (!slug && brand.mode === BRAND_MODES.NEUTRAL) {
        setState({ brand: fallbackBrand(hostname), settled: true });

        return;
      }

      writeCachedBrand(brand, hostname);
      setState({ brand, settled: true });
    });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, hostname]);

  // Logged in: brand from the session's provider. Right after login the
  // provider is null until /initial-data lands — keep the previous brand so
  // the login page's brand doesn't flash to neutral in between. Switching the
  // provider (super-admin sidebar select) re-themes and re-caches.
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const brand = brandFromServiceProvider(serviceProvider);

    if (brand) {
      writeCachedBrand(brand, hostname);
    }

    setState((current) => ({ brand: brand ?? current.brand, settled: true }));
  }, [isLoggedIn, serviceProvider, hostname]);

  const { brand, settled } = state;

  useEffect(() => {
    if (brand.mode === BRAND_MODES.FOOD_AMIGOS) {
      clearBrandColor();

      return;
    }

    applyBrandColor(brand.primaryColor);
  }, [brand]);

  // The UI language follows the brand: a language the brand does not offer (a German
  // session opening a Spanish partner's login) switches to the brand default — the
  // partner's language once translated, English until then.
  useEffect(() => {
    // `isInitialized` guards renders before Localization.js ran (unit tests).
    if (!settled || !i18n.isInitialized) {
      return;
    }

    const language = resolveBrandLanguage(brand, i18n.language);

    if (language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, [brand, settled]);

  const value = useMemo(
    () => ({
      ...brand,
      isWhiteLabel: brand.mode !== BRAND_MODES.FOOD_AMIGOS,
      isNeutral: brand.mode === BRAND_MODES.NEUTRAL,
      settled,
      partnerSlug,
    }),
    [brand, settled, partnerSlug],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
